import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import { selectUserPermissions, selectArePermissionsLoaded, selectPermissionsLoading } from '../modules/user/selectors';
import { selectUserProfile, ORG_USERTYPE, ORG_STAFF_USERTYPE, SUPERADMIN_USERTYPE } from '../modules/user';
import Loader from '../components/common/Loader';
import OrgDashboard from './Organization/Dashboard';
import SuperAdminDashboard from './SuperAdmin/Dashboard';

const moduleToPath: { [key: string]: string } = {
  // Org Modules
  'org-dashboard': '/dashboard',
  'worker-management': '/workers',
  'facility-management': '/facilities',
  'shift-monitoring': '/shifts-board',
  'shift-requests': '/shift-requests',
  'reports': '/org-report-analysis',
  'invoices': '/facility-invoices',
  'master-data': '/master-data',
  'settings': '/profile',
  // Super Admin Modules
  'super-admin-dashboard': '/dashboard',
  'system-roles': '/super-admin/system-roles',
  'system-modules': '/super-admin/system-modules',
  'role-module-mapping': '/super-admin/role-module-mapping',
  'user-management': '/super-admin/user-role-mapping',
  'subscription-management': '/super-admin/subscription-management',
};

const Dashboard = () => {
  const { setActivePage } = useSidebar();
  const { setPath } = useBreadcrumb();
  const permissions = useSelector(selectUserPermissions);
  const isLoaded = useSelector(selectArePermissionsLoaded);
  const loading = useSelector(selectPermissionsLoading);
  const userProfile = useSelector(selectUserProfile);
  const userType = userProfile?.data?.user_type;

  React.useEffect(() => {
    setActivePage('dashboard');
    setPath([{ title: 'Dashboard', path: '/' }]);
  }, [setActivePage, setPath]);

  // Wait for permissions to load before making a redirection decision
  if (loading || !isLoaded || !permissions) {
    return <Loader open={true} />;
  }

  // Check if current user type has dashboard access
  const dashboardSlug = (userType === ORG_USERTYPE || userType === ORG_STAFF_USERTYPE) ? 'org-dashboard' : 'super-admin-dashboard';
  const hasDashboardAccess = permissions?.[dashboardSlug]?.have_read || permissions?.[dashboardSlug]?.have_admin;

  if (!hasDashboardAccess && (userType === ORG_USERTYPE || userType === ORG_STAFF_USERTYPE || userType === SUPERADMIN_USERTYPE)) {
    const availableModules = Object.keys(permissions || {}).filter(slug => 
      (permissions?.[slug]?.have_read || permissions?.[slug]?.have_admin) && moduleToPath[slug]
    );

    if (availableModules.length > 0) {
      // Find the first module that isn't the dashboard itself (to avoid loop)
      const fallbackModule = availableModules.find(slug => slug !== dashboardSlug) || availableModules[0];
      return <Navigate to={moduleToPath[fallbackModule]} replace />;
    } else {
      // No accessible modules at all
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (userType === SUPERADMIN_USERTYPE) {
    return <SuperAdminDashboard />;
  }

  return <OrgDashboard />;
};

export default Dashboard;
